import { useLanguage } from "../i18n";
import { correctEvent, type CareEvent } from "../eventsApi";

const LABEL_KEYS: Record<string, keyof ReturnType<typeof useLanguage>["t"]> = {
  check: "actCheck",
  water: "actWater",
  feed: "actFeed",
  repot: "actRepot",
  prune: "actPrune",
  mist: "actMist",
  rotate: "actRotate",
  propagate: "actPropagate",
  pest_inspect: "actPestInspect",
  note: "actNote",
  move: "actMove",
  placement_accepted: "actAccepted",
  correction: "correctionLabel",
};

const SOIL_LABELS: Record<string, string> = {
  wet: "wet",
  moist: "slightly moist",
  dry: "dry",
  very_dry: "very dry",
  not_checked: "not checked",
};

function detail(event: CareEvent): string | null {
  const parts: string[] = [];
  const soil = event.payload.soil;
  if (typeof soil === "string") parts.push(`soil ${SOIL_LABELS[soil] ?? soil}`);
  const note = event.payload.note;
  if (typeof note === "string") parts.push(note);
  // The number he chose, so changing his mind is legible as a sequence rather
  // than as two identical-looking rows.
  const days = event.payload.interval_days;
  if (typeof days === "number" && days > 0) parts.push(`every ${days} days`);
  return parts.length ? parts.join(" — ") : null;
}

interface MovedLocation {
  id?: string;
  ar?: string;
  en?: string;
  placement?: string;
}

/**
 * A move reads as the journey it was: البلكونة → الاستقبال.
 *
 * The arrow points the way the page runs, because "from → to" reversed is a
 * different claim, not a stylistic variant. Both names come out of the event's
 * own payload rather than a lookup, so the line still reads if a Location is
 * ever renamed or removed.
 */
function Moved({ event }: { event: CareEvent }) {
  const { pick } = useLanguage();
  const { samePlacement } = useLanguage().t;
  const from = event.payload.from as MovedLocation | undefined;
  const to = event.payload.to as MovedLocation | undefined;
  if (!to) return null;

  const name = (place: MovedLocation) => pick({ ar: place.ar ?? "", en: place.en ?? "" }) || place.en || place.ar || "—";
  // The Placement is the half that changes what the app asks for. Saying it
  // here is how a reader tells a move that mattered from one that did not.
  const bothKnown = Boolean(from?.placement && to.placement);
  const changedPlacement = bothKnown && from?.placement !== to.placement;

  return (
    <span className="event-detail event-move">
      {from && <span className="moved-place">{name(from)}</span>}
      {from && <span className="moved-arrow" aria-hidden="true">→</span>}
      <span className="moved-place">{name(to)}</span>
      {bothKnown &&
        (changedPlacement ? (
          <span className="moved-placement" dir="ltr">
            {from?.placement} → {to.placement}
          </span>
        ) : (
          <span className="moved-placement">{samePlacement}</span>
        ))}
    </span>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export function History({
  events,
  specimenId,
  onCorrected,
}: {
  events: CareEvent[];
  specimenId: string;
  onCorrected: () => void;
}) {
  const { t } = useLanguage();

  // Which events a correction has voided. Both rows stay visible — the point is
  // that the history still shows what happened, including the mistake.
  const voided = new Set(
    events
      .filter((event) => event.type === "correction")
      .map((event) => event.payload.voids)
      .filter((id): id is string => typeof id === "string"),
  );
  if (events.length === 0) {
    return (
      <section className="history">
        <h2>{t.history}</h2>
        <p className="muted">{t.historyEmpty}</p>
      </section>
    );
  }

  return (
    <section className="history">
      <h2>{t.history}</h2>
      <ol className="events">
        {events.map((event) => (
          <li key={event.id} className={voided.has(event.id) ? "voided" : undefined}>
            <time dateTime={event.occurred_at}>{when(event.occurred_at)}</time>
            <span className="event-type">{String(t[LABEL_KEYS[event.type]] ?? event.type)}</span>
            {event.type === "correction" && <span className="event-detail">{t.voided}</span>}
            {voided.has(event.id) && <span className="event-source">{t.corrected}</span>}
            {event.type === "move" && <Moved event={event} />}
            {detail(event) && <span className="event-detail">{detail(event)}</span>}
            {/* Says the record was entered after the fact, so an estimated
                date is never mistaken for one logged as it happened. */}
            {event.source === "manual_backfill" && <span className="event-source">{t.enteredLater}</span>}
            {event.source === "telegram" && <span className="event-source">{t.viaTelegram}</span>}
            {/* Not offered on a move: a correction voids the record but cannot
                carry the plant back, so taking it would leave the engine
                scheduling for a room the plant is not in. Moving it back is
                both the fix and the truth. */}
            {event.type === "move" && !voided.has(event.id) && (
              <span className="event-source">{t.moveBackInstead}</span>
            )}
            {/* Not a delete: the record stays and a correction is appended. */}
            {event.type !== "correction" && event.type !== "move" && !voided.has(event.id) && (
              <button
                type="button"
                className="correct"
                onClick={() => void correctEvent(specimenId, event.id).then(onCorrected)}
              >
                {t.correct}
              </button>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
